// The agents of other houses you hold: sell each back, let it go, or put it to
// death (module `espionage.counter_intel`). Left alone, the sim lets them go.

import { useState } from 'react';
import type { HeldAgent } from '../../espionage/espionage.js';
import { type MessageKey, useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import type { AgentChoice } from '../../sim/useSimWorld/applyIntent.js';
import type { OpsActions } from '../../sim/useSimWorld/useEspionageIntents.js';

const BUTTON =
  'rounded border border-border/60 px-2 py-0.5 text-left text-[10px] hover:bg-muted/60';

export function HeldAgents({
  agents,
  ransom,
  nameOf,
  actions,
}: {
  readonly agents: readonly HeldAgent[];
  readonly ransom: number;
  readonly nameOf: (seat: number) => string;
  readonly actions: OpsActions;
}) {
  const t = useT();
  const [refused, setRefused] = useState<number | null>(null);
  const choose = (id: number, choice: AgentChoice) =>
    setRefused(actions.decideAgent(id, choice) ? null : id);
  const choices: readonly [AgentChoice, string][] = [
    ['ransom', t('espionage.agentRansom', { gold: ransom })],
    ['release', t('espionage.agentRelease')],
    ['execute', t('espionage.agentExecute')],
  ];
  return (
    <section className="flex flex-col gap-1.5" data-testid="held-agents">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t('espionage.agents')}
      </h3>
      {agents.map((a) => (
        <div key={a.id} className="flex flex-col gap-1 rounded border border-border/60 p-1.5">
          <span className="text-xs">
            {t('espionage.agentOf', {
              kind: t(`espionage.agentKind.${a.kind}` as MessageKey),
              house: nameOf(a.owner),
            })}
          </span>
          <div className="flex flex-wrap gap-1">
            {choices.map(([choice, label]) => (
              <button
                key={choice}
                type="button"
                data-testid={`agent-${choice}-${a.id}`}
                {...appDom({
                  id: `agent-${choice}-${a.id}`,
                  type: 'Button',
                  role: 'button',
                  scope: 'section',
                })}
                onClick={() => choose(a.id, choice)}
                className={BUTTON}
              >
                {label}
              </button>
            ))}
          </div>
          {refused === a.id && (
            <p aria-live="polite" className="text-[10px] text-red-700 dark:text-red-300">
              {t('espionage.agentRefused')}
            </p>
          )}
        </div>
      ))}
    </section>
  );
}
